import { Box, Button, ButtonGroup, Stack, useTheme } from "@mui/material";
import { AxiosInstance } from "axios";
import { useEffect, useState } from "react";
import Panel from "../components/Panel";
import Chart from "../components/Chart";
import { MetricsHistory, MetricsHistoryPoint } from "../types";

const WINDOWS = ["1h", "6h", "24h"];

interface TrendsPanelProps {
    req: AxiosInstance;
    id: string;
    hasICAP: boolean;
}

// TrendsPanel pulls the ES-backed metrics history and renders interactive charts.
// "Current" numbers come from Redis (other panels); these charts come from ES.
export default function TrendsPanel({ req, id, hasICAP }: TrendsPanelProps) {
    const theme = useTheme();
    const [window, setWindow] = useState("1h");
    const [history, setHistory] = useState<MetricsHistory | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        req.get<MetricsHistory>(`/api/services/${encodeURIComponent(id)}/history`, { params: { window } })
            .then((resp) => { if (active) { setHistory(resp.data); setError(false); } })
            // Keep any existing data and surface the failure instead of masquerading as
            // an empty metrics window. Global 401 handling lives in the axios instance.
            .catch(() => { if (active) setError(true); });
        return () => { active = false; };
    }, [req, id, window]);

    const points = history?.points || [];
    const times = points.map((p) => p.timestamp);
    const col = (pick: (p: MetricsHistoryPoint) => number | undefined) => points.map((p) => pick(p) ?? null);

    // A max/capacity of 0 means "unlimited" — don't draw a misleading flat-0 limit line.
    const hasConnLimit = points.some((p) => (p.maxConnections ?? 0) > 0);
    const hasQueueLimit = points.some((p) => (p.queueCapacity ?? 0) > 0);
    const limitColor = theme.palette.text.secondary;

    return (
        <Panel title="Trends">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ color: "error.main", fontSize: 13 }}>
                    {error ? "Could not load trends — showing last available data." : ""}
                </Box>
                <ButtonGroup size="small" variant="outlined">
                    {WINDOWS.map((w) => (
                        <Button key={w} variant={window === w ? "contained" : "outlined"} onClick={() => setWindow(w)}>{w}</Button>
                    ))}
                </ButtonGroup>
            </Stack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {hasICAP && (
                    <Chart title="Active connections" times={times} series={[
                        { label: "peak", color: theme.palette.primary.main, values: col((p) => p.activeMax) },
                        { label: "avg", color: theme.palette.info.main, values: col((p) => p.activeAvg) },
                        ...(hasConnLimit ? [{ label: "limit", color: limitColor, values: col((p) => p.maxConnections), dashed: true }] : []),
                    ]} />
                )}
                {hasICAP && (
                    <Chart title="Queue depth" times={times} series={[
                        { label: "peak", color: theme.palette.warning.main, values: col((p) => p.queueMax) },
                        ...(hasQueueLimit ? [{ label: "limit", color: limitColor, values: col((p) => p.queueCapacity), dashed: true }] : []),
                    ]} />
                )}
                <Chart title="Upstream latency" unit=" ms" times={times} series={[
                    { label: "Redis", color: theme.palette.success.main, values: col((p) => p.redisPingMs) },
                    { label: "ES", color: theme.palette.secondary.main, values: col((p) => p.esPingMs) },
                ]} />
            </Box>
        </Panel>
    );
}
